import { MovieClip } from '@play-co/timestep-core/ui';
import { ButtonScaleViewOpts } from 'src/lib/ui/components/ButtonScaleView';
import {
  canSeeLeague,
  getEndedLeagues,
  getUnclaimedLeagues,
} from 'src/replicant/match2/leagues/leagues.getters';
import { createEmitter } from 'src/lib/Emitter';
import { leaderboardSize } from 'src/replicant/match2/leagues/leagues.ruleset';
import { AssetGroup } from '@play-co/timestep-core';
import { FeatureButton } from 'src/game/components/FeatureButton';
import { isSceneEntered } from 'src/lib/stateUtils';
import {
  getLeagueLeaderboard,
  getVisibleLeagueLeaderboard,
} from 'src/game/minigame/match2-timestep/leagues/league.sequences';

const assets = {
  movieclip: {
    url: 'assets/ui/match2/leagues/button',
    loadMethod: MovieClip.animationLoader,
  },
  ribbon: 'assets/ui/match2/leagues/ribbon.png',
};

export class ChampionsLeagueButton extends FeatureButton {
  private mode: 'ongoing' | 'claim' | 'unavailable' = 'unavailable';
  private refreshCount = 0;

  constructor(opts: ButtonScaleViewOpts) {
    super({
      width: 146,
      height: 154,
      pressedOffsetY: 5,
      labelPanel: {
        width: 98,
        height: 31,
        offsetX: -2,
        offsetY: -14,
        labelOffsetX: -1,
        labelOffsetY: -1,
        font: 'Title',
        background: {
          image: assets.ribbon,
        },
      },
      ...opts,
    });

    this.animationStateMap = {
      main: {
        type: 'movieclip',
        assetGroup: new AssetGroup(Object.values(assets)),
        url: assets.movieclip.url,
        sequence: ['masters_league_spawn', 'masters_league_loop'],
        scale: 0.65,
      },
    };

    this.container.updateOpts({
      offsetY: 7,
    });

    this.hide();

    createEmitter(this, ({ user }) => ({
      hasUnclaimedLeagues:
        getEndedLeagues(user).length > 0 ||
        getUnclaimedLeagues(user).length > 0,
      canSee: canSeeLeague(user),
      leagues: getLeagueLeaderboard().getCached(),
    })).addListener(this.refresh);
  }

  private refresh = async (args: {
    hasUnclaimedLeagues: boolean;
    canSee: boolean;
    leagues?: { leaderboard: unknown[]; playerRank: number };
  }) => {
    const refreshId = ++this.refreshCount;
    const { hasUnclaimedLeagues, canSee } = args;

    if (hasUnclaimedLeagues) {
      // Only load play once, if mode is not claim
      if (this.mode !== 'claim') {
        this.mode = 'claim';
        await this.setAnimationState('main');
      }

      this.updateOpts({
        visible: true,
      });
      this.labelPanel?.updateOpts({
        visible: true,
      });
      this.setLabelLocaleText('match2.tournament.claim');
      return;
    }

    const leagues = await getVisibleLeagueLeaderboard(canSee);
    if (refreshId !== this.refreshCount) return;

    // If nothing to claim, this button can only show in bedroom
    if (!isSceneEntered('bedroom')) {
      this.updateOpts({
        visible: false,
      });
      return;
    }

    const visible = canSee && !!leagues?.leaderboard.length;
    this.updateOpts({
      visible,
    });

    // Only load play once, if mode is not ongoing
    if (visible && this.mode !== 'ongoing') {
      this.mode = 'ongoing';
      await this.setAnimationState('main');
    }

    const showRank = !!(
      canSee &&
      leagues &&
      leagues.playerRank <= leaderboardSize
    );
    this.labelPanel?.updateOpts({
      visible: showRank,
    });

    if (!showRank) return;

    this.setLabelText(
      leagues.playerRank <= leaderboardSize
        ? `#${leagues!.playerRank.toString()}`
        : '-',
    );
  };
}
